import React, { useEffect, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { DealActivityLinkType } from '@trnsact/trnsact-shared-types';
import { APIRequestSort, ModalsKeys, Nullable, TabsOption } from 'global';
import { Email, LocalPhone, Sms, TextSnippet } from '@mui/icons-material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { portalConfigurationsSelectors } from 'redux/portalConfigurationReducer';

import { useConversationCreated, useDealActivityUpdated, useSmsActivityReceived } from 'hooks/useSubscriptions';
import { TabContainer } from './TabContainer';
import { useFetchActivityList } from '../api';
import { initialFilters } from '../constants';
import { MessagingPromo } from './MessagingPromo';
import { DealActivityList } from './DealActivityList';
import { SmsConversationCard } from './smsConversation';
import { DealActivityFilters } from './DealActivityFilters';
import { TabsContainer } from '../../../components/shared/TabsContainer/TabsContainer';
import { ActivityListFilters, ActivityPageTabs, ActivityType, SmsConversationData } from '../types';
import { DealSmsActivity } from '../api/types';

interface Props {
  parentId: string;
  parentType: DealActivityLinkType;
  onRegisterAgentOpener?: (fn: VoidFunction | null) => void;
}

export const DealActivityCard = ({ parentId, parentType, onRegisterAgentOpener }: Props) => {
  const { handleOpen: onOpenDialog } = useModal(ModalsKeys.ActivityDialog);
  const { handleOpen: onOpenCallDialog } = useModal(ModalsKeys.CallActivityDialog);
  const { handleOpen: onOpenNoteDialog } = useModal(ModalsKeys.NoteActivityDialog);
  const { handleOpen: onOpenNewChatDialog } = useModal(ModalsKeys.NewMessageDialog);

  const [currentTab, setCurrentTab] = useState<ActivityPageTabs>(ActivityPageTabs.ALL);

  const [sorting, setSorting] = useState<APIRequestSort<'created_date_time'>>({
    key: 'created_date_time',
    value: 'desc',
  });

  const [filters, setFilters] = useState<ActivityListFilters>(initialFilters);
  const [selectedConversation, setSelectedConversation] = useState<Nullable<SmsConversationData>>(null);

  const isMessagingEnabled = useSelector<any, boolean>(portalConfigurationsSelectors.allowMessagingModule);
  const accountId: string | null = useSelector((state: any) => state.userProfile?.accountId ?? null);

  const { activityList, loading, refetch } = useFetchActivityList({
    sorting,
    filters,
    parentId,
    parentType,
    activityType: currentTab,
  });

  // useEffect is used instead of onData to avoid stale-closure issues in React 17.
  const { data: latestSmsEvent } = useSmsActivityReceived(parentId);
  useEffect(() => {
    if (latestSmsEvent?.smsActivityReceived) {
      console.log('[subscription] smsActivityReceived', latestSmsEvent.smsActivityReceived);
      refetch();
    }
  }, [latestSmsEvent]);

  const { data: latestActivityEvent } = useDealActivityUpdated(parentId);
  useEffect(() => {
    if (latestActivityEvent?.dealActivityUpdated) {
      console.log('[subscription] dealActivityUpdated', latestActivityEvent.dealActivityUpdated);
      refetch();
    }
  }, [latestActivityEvent]);

  const { data: latestConversationEvent } = useConversationCreated(accountId);
  useEffect(() => {
    if (latestConversationEvent?.conversationCreated?.vendorOpportunityId === parentId) {
      console.log('[subscription] conversationCreated', latestConversationEvent.conversationCreated);
      refetch();
    }
  }, [latestConversationEvent]);

  useEffect(() => {
    if (!onRegisterAgentOpener) return;
    const agentSms = activityList.find(
      (a): a is DealSmsActivity => a.type === ActivityType.SMS && (a as DealSmsActivity).isAgent
    );
    if (agentSms) {
      onRegisterAgentOpener(() => {
        setSelectedConversation({
          voId: agentSms.parentId,
          conversationSequenceNumber: agentSms.conversationSequenceNumber,
          contactName: 'AI Agent',
          contactId: null,
          contactPhone: null,
          contactTitle: null,
          contactInitials: 'AI',
          isAgent: true,
          demoMode: true,
        });
      });
    } else {
      onRegisterAgentOpener(null);
    }
  }, [activityList, onRegisterAgentOpener]);

  const tabs: TabsOption<ActivityPageTabs>[] = [
    {
      label: 'All Activities',
      value: ActivityPageTabs.ALL,
      component: (
        <TabContainer
          actionName="ADD NEW"
          action={() => onOpenDialog({ parentId, parentType })}
          tabTitle={
            <Typography variant="h6" component="span">
              All Activity
            </Typography>
          }
          listSlot={
            <DealActivityList
              parentId={parentId}
              list={activityList}
              loading={loading}
              parentType={parentType}
              onOpenConversation={setSelectedConversation}
            />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Email',
      iconStart: <Email fontSize="small" />,
      value: ActivityPageTabs.EMAIL,
      component: (
        <TabContainer
          tabTitle="Email"
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Messages',
      iconStart: <Sms fontSize="small" />,
      value: ActivityPageTabs.SMS,
      component: isMessagingEnabled ? (
        <TabContainer
          tabTitle="Messages"
          action={() => onOpenNewChatDialog({ voId: parentId, activityList, onConfirm: setSelectedConversation })}
          actionName="NEW MESSAGE"
          listSlot={
            <DealActivityList
              parentId={parentId}
              list={activityList}
              loading={loading}
              parentType={parentType}
              onOpenConversation={setSelectedConversation}
            />
          }
        />
      ) : (
        <MessagingPromo />
      ),
    },
    {
      label: 'Call',
      iconStart: <LocalPhone fontSize="small" />,
      value: ActivityPageTabs.CALL,
      component: (
        <TabContainer
          tabTitle="Call"
          actionName="ADD NEW CALL"
          action={() => onOpenCallDialog({ parentId, parentType })}
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
    {
      label: 'Notes',
      iconStart: <TextSnippet fontSize="small" />,
      value: ActivityPageTabs.NOTE,
      component: (
        <TabContainer
          tabTitle="Notes"
          action={() => onOpenNoteDialog({ parentId, parentType })}
          actionName="ADD NEW NOTE"
          listSlot={
            <DealActivityList parentId={parentId} list={activityList} loading={loading} parentType={parentType} />
          }
          filterSlot={
            <DealActivityFilters
              type={currentTab}
              loading={loading}
              onFiltersChanged={setFilters}
              onSortingChanged={setSorting}
            />
          }
        />
      ),
    },
  ];

  return (
    <CardContainer>
      {selectedConversation ? (
        <SmsConversationCard
          data={selectedConversation}
          parentType={parentType}
          onBack={() => {
            setSelectedConversation(null);
            refetch();
          }}
        />
      ) : (
        <TabsContainer<ActivityPageTabs> tabs={tabs} onTabChange={setCurrentTab} />
      )}
    </CardContainer>
  );
};
